import Phaser from 'phaser';
import type { AuthorStats, Course, CourseRaw } from './types';

const STAT_KEY_ALIAS: Record<string, keyof AuthorStats> = {
  dialog: 'dialog',
  plot: 'plot',
  karakter: 'karakter',
  'språk': 'spraak',
  spraak: 'spraak',
  voice: 'voice',
  autentisitet: 'autentisitet',
  disiplin: 'disiplin',
  spenning: 'plot',
  humor: 'voice',
};

export const KURS_BOOST_CAP = 30;

export function skalerBoostsTilCap(
  boosts: Partial<AuthorStats>,
  cap: number,
): Partial<AuthorStats> {
  let total = 0;
  for (const v of Object.values(boosts)) {
    if (typeof v === 'number') total += v;
  }
  if (total <= cap || total <= 0) return { ...boosts };

  const faktor = cap / total;
  const ut: Partial<AuthorStats> = {};
  const keys = Object.keys(boosts) as (keyof AuthorStats)[];

  let fordelt = 0;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = boosts[k];
    if (typeof v !== 'number') continue;
    if (i === keys.length - 1) {
      ut[k] = Math.max(1, cap - fordelt);
    } else {
      const skalert = Math.max(1, Math.round(v * faktor));
      ut[k] = skalert;
      fordelt += skalert;
    }
  }
  return ut;
}

export class CourseCatalog {
  private _courses: Course[] = [];
  private _loaded = false;

  loadFromArray(raw: CourseRaw[]): void {
    this._courses = raw
      .map((c) => this.normalise(c))
      .filter((c): c is Course => c !== null);
    this._loaded = true;
  }

  async load(scene: Phaser.Scene, key = 'courses-json', url = 'data/courses.json'): Promise<void> {
    const cached = scene.cache.json.get(key) as CourseRaw[] | undefined;
    if (Array.isArray(cached)) {
      this.loadFromArray(cached);
      return;
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Klarte ikke laste ${url}: ${res.status}`);
    const data = (await res.json()) as CourseRaw[];
    this.loadFromArray(data);
  }

  get isReady(): boolean {
    return this._loaded;
  }

  get courses(): Course[] {
    return this._courses.map((c) => this.cloneCourse(c));
  }

  findById(id: string): Course | undefined {
    const found = this._courses.find((c) => c.id === id);
    return found ? this.cloneCourse(found) : undefined;
  }

  forSlott(stats: AuthorStats, antall: number): Course[] {
    if (this._courses.length === 0) return [];
    const ranked = this._courses
      .map((c) => ({ course: c, score: this.relevansScore(stats, c) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(0, antall));
    return ranked.map((r) => this.cloneCourse(r.course));
  }

  private relevansScore(stats: AuthorStats, course: Course): number {
    let score = 0;
    for (const [k, boost] of Object.entries(course.statBoosts)) {
      const key = k as keyof AuthorStats;
      const current = stats[key];
      if (boost === undefined || current === undefined) continue;
      const mangel = 100 - current;
      score += (boost / 100) * mangel;
    }
    return score;
  }

  private normalise(raw: CourseRaw): Course | null {
    const id = (raw.id ?? raw.slug ?? '').trim();
    const title = (raw.title ?? '').trim();
    const url = (raw.url ?? '').trim();
    const pris = Number(raw.price_nok ?? 0);
    const varighetUker = Number(raw.duration_weeks ?? 0);
    if (!id || !title || !varighetUker || !pris) return null;

    const statBoosts: Partial<AuthorStats> = {};
    const raw_boosts = raw.stat_boosts ?? {};
    for (const [k, v] of Object.entries(raw_boosts)) {
      const canonical = STAT_KEY_ALIAS[k.toLowerCase()];
      if (!canonical) continue;
      const verdi = Number(v);
      if (!Number.isFinite(verdi) || verdi <= 0) continue;
      statBoosts[canonical] = (statBoosts[canonical] ?? 0) + verdi;
    }

    const skalert = skalerBoostsTilCap(statBoosts, KURS_BOOST_CAP);

    return {
      id,
      title,
      varighetDager: varighetUker,
      pris,
      url,
      statBoosts: skalert,
    };
  }

  private cloneCourse(c: Course): Course {
    return { ...c, statBoosts: { ...c.statBoosts } };
  }
}

export const courseCatalog = new CourseCatalog();

export const STAT_LABEL: Record<keyof AuthorStats, string> = {
  dialog: 'Dialog',
  plot: 'Plot',
  karakter: 'Karakter',
  spraak: 'Språk',
  voice: 'Voice',
  autentisitet: 'Autentisitet',
  disiplin: 'Disiplin',
};
